import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Badge } from '@vnetwork/ui/primitives';
import { HardDrive } from 'lucide-react';
import { UsageMeter } from './UsageMeter';
import type { ZoneDatastore } from '@/api/opennebula/zones';

/** Storage pools holding VM disks, images and backups. */
interface Props {
  datastores: ZoneDatastore[];
}

function typeTone(type?: string): 'info' | 'success' | 'warning' | 'neutral' {
  switch (type?.toUpperCase()) {
    case 'IMAGE': return 'info';
    case 'SYSTEM': return 'success';
    case 'FILE': return 'warning';
    default: return 'neutral';
  }
}

const gb = (mb?: number): string => (mb == null ? '—' : `${(mb / 1024).toFixed(1)} GB`);
const clusters = (ids?: number[]): string => (Array.isArray(ids) && ids.length ? ids.join(', ') : '—');

const columns: ColumnDef<ZoneDatastore, unknown>[] = [
  { id: 'name', header: 'Name', cell: ({ row }) => <span style={{ fontWeight: 500 }}>{row.original.name}</span> },
  {
    id: 'type',
    header: 'Type',
    cell: ({ row }) => <Badge tone={typeTone(row.original.type)}>{row.original.type || 'OTHER'}</Badge>,
  },
  { id: 'clusters', header: 'Clusters', cell: ({ row }) => clusters(row.original.cluster_ids) },
  { id: 'total', header: 'Total', cell: ({ row }) => gb(row.original.total_mb) },
  {
    id: 'used',
    header: 'Used',
    cell: ({ row }) => (
      <UsageMeter
        used={Math.round(Number(row.original.used_mb ?? 0) / 1024)}
        total={Math.round(Number(row.original.total_mb ?? 0) / 1024)}
        suffix=" GB"
      />
    ),
  },
];

export function DatastoresTable({ datastores }: Props) {
  return (
    <DataTable
      columns={columns}
      data={datastores}
      getRowId={(d) => String(d.id)}
      emptyState={<EmptyState icon={<HardDrive size={28} />} title="No datastores" description="This zone has no storage pools." />}
    />
  );
}
